/**
 * LevelBadge — CSS "3D squircle" level badge.
 *
 * Web equivalent of client-native/src/.../LevelBadge3D.tsx. iOS draws a 3D
 * squircle via a 12-stroke SVG ladder; per the spec this is an accepted
 * KEEP-WEB simplification: a rounded-square (border-radius 35%) with a
 * top→bottom gradient + soft inset shadow + the level icon centered.
 *
 * Icon: when a per-level PNG `iconUrl` is supplied (LEVEL_ICONS parity —
 * client/src/assets/levels/, resolved by getLevelIconUrl) it is rendered at
 * 0.6× the badge (38px in a 64px badge, matching iOS). It falls back to the
 * level emoji (from @/data/parrotLevels) when no iconUrl is passed.
 *
 * Background per state: completed #E6F9E6, current #ffffff, future #f5f5f5.
 */
import { FC } from "react";
import { colors } from "@/ui";

export type LevelBadgeState = "completed" | "current" | "future";

export interface LevelBadgeProps {
  emoji: string;
  state: LevelBadgeState;
  /** Outer size in px (default 64, matching iOS). */
  size?: number;
  iconUrl?: string;
}

const STATE_BG: Record<LevelBadgeState, string> = {
  completed: colors.lightGreen, // #E6F9E6
  current: colors.white, // #ffffff
  future: colors.greyLightest, // #f5f5f5
};

const LevelBadge: FC<LevelBadgeProps> = ({
  emoji,
  state,
  size = 64,
  iconUrl,
}) => {
  const bg = STATE_BG[state];
  return (
    <div
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "35%",
        background: `linear-gradient(180deg, #ffffff 0%, ${bg} 100%)`,
        boxShadow:
          "inset 0 2px 4px rgba(255,255,255,0.9), inset 0 -3px 6px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.10)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        opacity: state === "future" ? 0.85 : 1,
      }}
    >
      {iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          aria-hidden
          style={{ width: size * 0.6, height: size * 0.6, objectFit: "contain" }}
        />
      ) : (
        <span style={{ fontSize: size * 0.5, lineHeight: 1 }} aria-hidden>
          {emoji}
        </span>
      )}
    </div>
  );
};

export default LevelBadge;
